import type { SmsCommandAck } from '@/modules/notifications/sms.api'
import { useCallback, useEffect, useState } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { smsSettingsApi } from '@/modules/notifications/sms.api'
import { hashKey } from '@tanstack/react-query'

import { useNotificationReadSession } from './useNotificationReadSession'
import { useNotificationScopedQuery } from './useNotificationScopedQuery'
import { useOwnedReaderMutation } from './useOwnedReaderMutation'

type SmsDraft = {
  key: string
  notice: string | null
  phoneNumber: string
  code: string
  consentAccepted: boolean
  challenge: Extract<SmsCommandAck, { kind: 'request' }> | null
}
/** Phone/code/consent are local input; status is solely an owned server reader. */
export function useSmsSettings(enabled = true) {
  const session = useNotificationReadSession(),
    key = hashKey([session.key])
  const query = useNotificationScopedQuery(
    queryKeys.notifications.smsStatus(),
    session,
    smsSettingsApi.status,
    { enabled },
  )
  const mutation = useOwnedReaderMutation(session, {
    execute: smsSettingsApi.command,
    queryRoot: queryKeys.notifications.all,
    singleFlight: true,
    successMessage: result =>
      result.kind === 'request'
        ? 'Verification code sent'
        : result.kind === 'confirm'
          ? 'SMS notifications are ready'
          : 'SMS notifications turned off',
    errorMessage:
      'The SMS change could not be confirmed. Refresh your status before trying again.',
  })
  const [stored, setStored] = useState<SmsDraft | null>(null)
  const fresh = (): SmsDraft => ({
    key,
    notice: null,
    phoneNumber: query.data?.phoneNumber ?? '',
    code: '',
    consentAccepted: false,
    challenge: null,
  })
  const draft = stored?.key === key ? stored : fresh()
  const patch = (changes: Partial<Omit<SmsDraft, 'key'>>) => {
    session.assertCurrent()
    setStored(previous => ({
      ...(previous?.key === key ? previous : fresh()),
      notice: null,
      ...changes,
    }))
  }
  useEffect(() => {
    const challenge = draft.challenge
    if (!challenge) return
    const timer = setTimeout(
      () => {
        try {
          session.assertCurrent()
        } catch {
          return
        }
        setStored(previous =>
          previous?.key === key &&
          previous.challenge?.expiresAt === challenge.expiresAt
            ? {
                ...previous,
                code: '',
                challenge: null,
                consentAccepted: false,
                notice: 'Verification code expired. Request a new code.',
              }
            : previous,
        )
      },
      Math.max(0, Date.parse(challenge.expiresAt) - Date.now()),
    )
    return () => clearTimeout(timer)
  }, [draft.challenge, key, session])
  const requestCode = async () => {
    const result = await mutation.mutateAsync({
      kind: 'request',
      phoneNumber: draft.phoneNumber,
      consentAccepted: draft.consentAccepted,
    })
    if (result.kind === 'request')
      patch({ phoneNumber: result.phoneNumber, challenge: result, code: '' })
  }
  const confirmCode = async () => {
    if (
      !draft.challenge ||
      draft.challenge.phoneNumber !== draft.phoneNumber ||
      Date.parse(draft.challenge.expiresAt) <= Date.now()
    )
      throw new Error('Request a new verification code')
    await mutation.mutateAsync({
      kind: 'confirm',
      phoneNumber: draft.challenge.phoneNumber,
      code: draft.code,
    })
    patch({ code: '', challenge: null, consentAccepted: false })
  }
  const revoke = async () => {
    await mutation.mutateAsync({ kind: 'revoke' })
    patch({ code: '', challenge: null, consentAccepted: false })
  }
  const retry = useCallback(async () => {
    session.assertCurrent()
    await query.refetch()
  }, [session, query.refetch])
  return {
    available: enabled && session.enabled,
    status: query.error || query.isFetching ? undefined : query.data,
    isLoading: query.isLoading || query.isFetching,
    error: query.error,
    isSaving: mutation.isPending,
    phoneNumber: draft.phoneNumber,
    code: draft.code,
    consentAccepted: draft.consentAccepted,
    codeSent: !!draft.challenge,
    notice: draft.notice,
    setPhoneNumber: (phoneNumber: string) => patch({ phoneNumber }),
    setCode: (code: string) => patch({ code }),
    setConsentAccepted: (consentAccepted: boolean) =>
      patch({ consentAccepted }),
    changeNumber: () =>
      patch({ code: '', challenge: null, consentAccepted: false }),
    requestCode,
    confirmCode,
    revoke,
    retry,
  }
}
